// Typed fetch helpers for the rust-dashboard agent REST endpoints.
//
// PR 2 of `auth-via-telegram-otp`. The dev-user impersonation path
// (X-Dev-User header / dashboard_dev_user cookie / auto-pick to
// "jack") is REMOVED. Mutating calls now read X-CSRF-Token from the
// in-memory auth store (authUser.csrf) and rely on the browser
// auto-sending the dashboard_session cookie on every fetch.
//
// The Rust agent exposes:
//
//   GET  /api/snapshot       → MetricSnapshot (latest cached host + containers)
//   GET  /healthz, /readyz   → plaintext
//   GET  /api/audit          → AuditView[] (NDJSON-backed, newest first)
//   GET  /api/session        → SessionInfo / 401 (legacy alias; prefer /api/auth/me)
//   GET  /api/auth/me        → SessionInfo / 401 (PR 2 contract)
//   POST /api/auth/request   → { ttl_secs } or 4xx (Telegram-OTP, see lib/auth.ts)
//   POST /api/auth/verify    → { user, csrf_token } + Set-Cookie or 4xx
//   POST /api/auth/logout    → 200 + Set-Cookie clears session
//   POST /api/{restart,stop,start}/{container} → { status }
//   POST /api/bulk           → BulkMutateResponse
//   GET  /api/fs/list?path=… → FsListResponse
//   POST /api/fs/mutate      → { status }
//   GET  /api/services       → SystemdUnit[]
//   POST /api/services/mutate → { status }
//   GET  /api/network        → NetworkSnapshot
//
// All responses are JSON. Error handling throws on non-2xx so the
// Svelte stores can surface the failure as a typed error state.

import { getValue } from "./store";
import { csrfHeader, authUser, authState } from "./auth";
import type {
  AuditView,
  MetricSnapshot,
  BulkMutateResponse,
  FsListResponse,
  SystemdUnit,
  NetworkSnapshot,
} from "./bindings";

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Build the base headers for every request. PR 2:
 *   - always: `Accept: application/json`
 *   - on non-GET: `X-CSRF-Token` from the auth store when a session
 *     is active (the agent rejects mutates without one with 419).
 *   - nothing else — the browser sends `Cookie: dashboard_session=…`
 *     automatically. No `X-Dev-User` (PR 1 removed the dev
 *     impersonation path; the env var is dropped in PR 2).
 */
function commonHeaders(extra?: HeadersInit, method?: string): HeadersInit {
  const h: Record<string, string> = {
    Accept: "application/json",
  };
  if (method && method.toUpperCase() !== "GET" && getValue(authState) === "authenticated") {
    Object.assign(h, csrfHeader());
  }
  return { ...h, ...(extra as Record<string, string> | undefined) };
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = init.method ?? "GET";
  const res = await fetch(path, {
    ...init,
    method,
    headers: commonHeaders(init.headers, method),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  return (await res.json()) as T;
}

export function getSnapshot(): Promise<MetricSnapshot> {
  return request<MetricSnapshot>("/api/snapshot");
}

/** One persisted log line from `GET /api/logs/{container}`. */
export interface LogEntry {
  ts_ms: number;
  line: string;
}

export async function getContainerLogs(
  container: string,
  opts: { minutes?: number; q?: string } = {},
): Promise<LogEntry[]> {
  const u = new URL(`/api/logs/${encodeURIComponent(container)}`, window.location.href);
  if (opts.minutes) u.searchParams.set("minutes", String(opts.minutes));
  if (opts.q) u.searchParams.set("q", opts.q);
  const res = await request<{ container: string; lines: LogEntry[] }>(u.pathname + u.search);
  return res.lines;
}

export type GroupsMap = Record<string, string[]>;

export function getGroups(): Promise<GroupsMap> {
  return request<{ groups: GroupsMap }>("/api/groups").then((r) => r.groups);
}

export async function putGroup(name: string, patterns: string[]): Promise<GroupsMap> {
  const res = await request<{ groups: GroupsMap }>(`/api/groups/${encodeURIComponent(name)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ patterns }),
  });
  return res.groups;
}

export async function deleteGroup(name: string): Promise<GroupsMap> {
  const res = await request<{ groups: GroupsMap }>(`/api/groups/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
  return res.groups;
}

export function getAudit(limit: number = 200): Promise<AuditView[]> {
  const u = new URL("/api/audit", window.location.href);
  u.searchParams.set("limit", String(limit));
  return request<AuditView[]>(u.pathname + u.search);
}

/** Same wire format as `getAudit`; the new name reads better against
 * the dedicated `lib/audit.ts` wrapper. */
export function auditList(filters: {
  action?: string;
  user?: string;
  limit?: number;
} = {}): Promise<AuditView[]> {
  const u = new URL("/api/audit", window.location.href);
  if (filters.action) u.searchParams.set("action", filters.action);
  if (filters.user) u.searchParams.set("user", filters.user);
  if (typeof filters.limit === "number") {
    u.searchParams.set("limit", String(filters.limit));
  }
  return request<AuditView[]>(u.pathname + u.search);
}

/**
 * Legacy /api/session probe — kept for backwards compatibility with
 * code that imports it directly, but the SPA's auth flow now uses
 * `getSession()` from `lib/auth.ts` (which calls `/api/auth/me`).
 * Returns `{ user, csrf_token }` or throws an `ApiError(401)` when no
 * session is active.
 */
export async function getSessionLegacy(): Promise<{
  user: string;
  csrf_token: string;
}> {
  return request<{ user: string; csrf_token: string }>("/api/session");
}

/**
 * Legacy session bootstrap — PR 1 callers used this to mint a fresh
 * `dashboard_session` cookie + CSRF pair (and read the cookie) before
 * a mutate. PR 2 ships the CSRF token (and the cookie) directly via
 * `authUser`, so this is now a thin wrapper that returns the active
 * session from the in-memory store. Throws an `ApiError(401)` if no
 * session is active so legacy call sites keep their existing error
 * branches.
 */
export async function createSession(): Promise<{
  user: string;
  csrf_token: string;
}> {
  const current = getValue(authUser);
  if (!current) {
    throw new ApiError(401, "no active session");
  }
  return { user: current.user, csrf_token: current.csrf };
}

export interface MutateArgs {
  action: "restart" | "stop" | "start";
  container: string;
  csrf: string;
  cookie: string;
}

/**
 * Single mutate (restart/stop/start). PR 2 wires only the session
 * cookie + X-CSRF-Token; the previous `X-2FA-Token` (per-action TOTP)
 * is dropped because the agent's mutate gate no longer reads it.
 */
export function mutateOne(args: MutateArgs): Promise<{ status: string }> {
  const u = new URL(
    `/api/${args.action}/${encodeURIComponent(args.container)}`,
    window.location.href,
  );
  return request<{ status: string }>(u.pathname, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": args.csrf,
    },
    body: JSON.stringify({ action: args.action }),
  });
}

export interface BulkArgs {
  action: "restart" | "stop" | "start";
  containers: string[];
  csrf: string;
  cookie: string;
}

export function mutateBulk(args: BulkArgs): Promise<BulkMutateResponse> {
  return request<BulkMutateResponse>("/api/bulk", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": args.csrf,
    },
    body: JSON.stringify({
      action: args.action,
      containers: args.containers,
    }),
  });
}

export function getFsList(path: string): Promise<FsListResponse> {
  const u = new URL("/api/fs/list", window.location.href);
  if (path) u.searchParams.set("path", path);
  return request<FsListResponse>(u.pathname + u.search);
}

/** PR 4 storage: list a directory via `GET /api/fs/list?path=…`. */
export function fsList(path: string): Promise<FsListResponse> {
  return getFsList(path);
}

export interface FsMutateArgs {
  action: "create" | "delete" | "chmod";
  path: string;
  is_dir?: boolean;
  mode?: number;
  csrf: string;
}

export function mutateFs(args: FsMutateArgs): Promise<{ status: string }> {
  return request<{ status: string }>("/api/fs/mutate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: args.action,
      path: args.path,
      is_dir: args.is_dir,
      mode: args.mode,
      csrf: args.csrf,
    }),
  });
}

/**
 * PR 4 storage save gate: write a file's content to `/api/fs/mutate`.
 *
 *   POST /api/fs/mutate
 *   Headers: X-CSRF-Token, Content-Type
 *   Body:    { path: string, content: string, csrf: string }
 *   200 OK  → { status: "ok", saved_at: number }
 */
export interface FsContentMutateArgs {
  path: string;
  content: string;
  csrf: string;
}

export interface FsContentMutateResponse {
  status: string;
  saved_at: number;
}

export function fsMutate(
  args: FsContentMutateArgs,
): Promise<FsContentMutateResponse> {
  return request<FsContentMutateResponse>("/api/fs/mutate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      path: args.path,
      content: args.content,
      csrf: args.csrf,
    }),
  });
}

/** Maximum editor buffer size before the client refuses to POST
 * the body. Matches the design contract — files above this cap
 * surface a "too large" error before any mutate fires. */
export const FS_MAX_CONTENT_BYTES = 1024 * 1024; // 1 MB

export function getServicesList(): Promise<SystemdUnit[]> {
  return request<SystemdUnit[]>("/api/services");
}

/** PR 5 alias for `getServicesList`. */
export function servicesList(): Promise<SystemdUnit[]> {
  return getServicesList();
}

export interface ServiceMutateArgs {
  action: "start" | "stop" | "restart" | "reload";
  unit: string;
  csrf: string;
}

export function mutateService(
  args: ServiceMutateArgs,
): Promise<{ status: string }> {
  return request<{ status: string }>("/api/services/mutate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: args.action,
      unit: args.unit,
      csrf: args.csrf,
    }),
  });
}

/** PR 5 alias for `mutateService`. */
export function servicesMutate(
  args: ServiceMutateArgs,
): Promise<{ status: string }> {
  return mutateService(args);
}

export function getNetworkSnapshot(): Promise<NetworkSnapshot> {
  return request<NetworkSnapshot>("/api/network");
}

// PR 2 leftover surface — kept so existing imports keep compiling.
// `authUser` and `csrfHeader` are read by other helpers (e.g.
// `lib/mutate.ts`); `getValue` is exported via the store module so
// the surface stays narrow.
export { authUser, csrfHeader, getValue };
